import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import { getBaseUrl } from '../../../shared/utils/apiConfig';
import { safeDecodeJWT, isJWTExpired } from '../../../shared/utils/jwtHelpers';
import {
  SessionTimeoutConfig,
  SessionTimeoutState,
  initializeSession,
  restoreSessionState,
  checkSessionTimeout,
  clearSessionData,
  createActivityListeners
} from '../../../shared/utils/sessionTimeout';

/**
 * Platform Admin Authentication Context
 * 
 * Manages authentication state for platform administrators.
 * Separate from tenant user authentication.
 */

interface PlatformAdminUser {
  id: string;
  username: string;
  email: string;
  is_platform_admin: boolean;
  is_super_admin: boolean;
  permissions: Record<string, boolean>;
}

interface PlatformAdminAuthContextType {
  platformAdmin: PlatformAdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAuthInitialized: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  verifyToken: () => Promise<boolean>;
  isSessionExpired: boolean;
  navigateToLogin: () => void;
}

const PlatformAdminAuthContext = createContext<PlatformAdminAuthContextType | undefined>(undefined);

// Session timeout configuration
const SESSION_CONFIG: SessionTimeoutConfig = {
  idleTimeoutMinutes: 30,
  absoluteTimeoutMinutes: 12 * 60, // 12 hours
  storageKeyPrefix: 'platform_admin'
};

const TOKEN_STORAGE_KEY = 'astrapeak_platform_admin_token';

export const PlatformAdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [platformAdmin, setPlatformAdmin] = useState<PlatformAdminUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const [sessionState, setSessionState] = useState<SessionTimeoutState>({
    lastActivityTime: null,
    sessionStartTime: null
  });
  const tokenCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const activityCleanupRef = useRef<(() => void) | null>(null);

  const verifyToken = useCallback(async (): Promise<boolean> => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    
    if (!token) {
      setPlatformAdmin(null);
      setIsLoading(false);
      return false;
    }

    try {
      const response = await fetch(`${getBaseUrl()}/api/platform-admin/auth/verify`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Token verification failed');
      }

      const data = await response.json();

      if (!data.valid || !data.is_platform_admin) {
        throw new Error('Invalid platform admin token');
      }

      const user: PlatformAdminUser = {
        id: data.user_id,
        username: data.username,
        email: data.email,
        is_platform_admin: data.is_platform_admin,
        is_super_admin: data.is_super_admin,
        permissions: data.permissions || {},
      };

      setPlatformAdmin(user);
      localStorage.setItem('platform_admin_user', JSON.stringify(user));
      setIsLoading(false);
      return true;
    } catch (error) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem('astrapeak_platform_admin_refresh_token');
      localStorage.removeItem('platform_admin_user');
      sessionStorage.removeItem('platform_admin_authed');
      clearSessionData(SESSION_CONFIG);
      setPlatformAdmin(null);
      setIsLoading(false);
      return false;
    }
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/api/platform-admin/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Authentication failed' }));
      throw new Error(errorData.detail || 'Invalid username or password');
    }

    const data = await response.json();

    if (!data.is_platform_admin) {
      throw new Error('Invalid platform admin credentials');
    }

    localStorage.setItem(TOKEN_STORAGE_KEY, data.access_token);
    localStorage.setItem('astrapeak_platform_admin_refresh_token', data.refresh_token);

    const tokenPayload = safeDecodeJWT(data.access_token);
    if (!tokenPayload) {
      throw new Error('Failed to decode platform admin token');
    }

    const user: PlatformAdminUser = {
      id: tokenPayload.sub,
      username: tokenPayload.username,
      email: tokenPayload.email,
      is_platform_admin: true,
      is_super_admin: tokenPayload.is_super_admin,
      permissions: tokenPayload.permissions || {},
    };

    setPlatformAdmin(user);
    localStorage.setItem('platform_admin_user', JSON.stringify(user));
    sessionStorage.setItem('platform_admin_authed', 'true');
    
    // Initialize session tracking using shared utility
    const newSessionState = initializeSession(SESSION_CONFIG);
    setSessionState(newSessionState);
    setIsSessionExpired(false);
  }, []);

  const navigateToLogin = useCallback(() => {
    window.location.href = '/platform-admin/login';
  }, []);

  const logout = useCallback(() => {
    // Clear all authentication data
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem('astrapeak_platform_admin_refresh_token');
    localStorage.removeItem('platform_admin_user');
    sessionStorage.removeItem('platform_admin_authed');
    clearSessionData(SESSION_CONFIG);
    
    // Clear interval if exists
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
      tokenCheckIntervalRef.current = null;
    }
    
    // Clear activity listeners if exist
    if (activityCleanupRef.current) {
      activityCleanupRef.current();
      activityCleanupRef.current = null;
    }
    
    // Reset state
    setPlatformAdmin(null);
    setIsSessionExpired(true);
    setSessionState({ lastActivityTime: null, sessionStartTime: null });
    
    // Redirect to login using window.location to avoid useNavigate dependency
    navigateToLogin();
  }, [navigateToLogin]);

  // Initialize session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      await verifyToken();
      setIsAuthInitialized(true);
    };
    
    initializeAuth();
    
    // Restore session state using shared utility
    const restoredState = restoreSessionState(SESSION_CONFIG);
    setSessionState(restoredState);
  }, [verifyToken]);
  
  // Token expiration and session timeout monitoring
  // CRITICAL FIX: Use ref to store sessionState to avoid stale closures
  const sessionStateRef = useRef<SessionTimeoutState>(sessionState);
  
  useEffect(() => {
    sessionStateRef.current = sessionState;
  }, [sessionState]);
  
  useEffect(() => {
    if (!platformAdmin || isSessionExpired) {
      return;
    }
    
    const checkTokenAndSession = () => {
      // Use shared utility for session timeout checking
      // Read from ref to get current value, not stale closure
      const timeoutCheck = checkSessionTimeout(
        SESSION_CONFIG,
        sessionStateRef.current,
        TOKEN_STORAGE_KEY,
        isJWTExpired
      );
      
      if (timeoutCheck.isExpired) {
        logout();
      }
    };
    
    // Check immediately
    checkTokenAndSession();
    
    // Check every minute - interval is stable because dependencies don't change
    if (!tokenCheckIntervalRef.current) {
      tokenCheckIntervalRef.current = setInterval(checkTokenAndSession, 60 * 1000);
    }
    
    return () => {
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
        tokenCheckIntervalRef.current = null;
      }
    };
  }, [platformAdmin, isSessionExpired, logout]);
  
  // Track user activity
  // CRITICAL FIX: Use ref for cleanup to prevent memory leaks
  // CRITICAL FIX 2: Debounce activity updates to prevent re-render loop causing UI flickering
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedActivityUpdate = useCallback(
    debounce(() => {
      setSessionState(prev => ({
        ...prev,
        lastActivityTime: new Date()
      }));
    }, 5000), // Only update every 5 seconds max - prevents constant re-renders
    []
  );
  
  useEffect(() => {
    if (!platformAdmin || isSessionExpired) {
      return;
    }
    
    // Use debounced handler to prevent constant re-renders from mouse/keyboard events
    const handleUserActivity = () => {
      debouncedActivityUpdate();
    };
    
    // Use shared utility for activity listeners
    activityCleanupRef.current = createActivityListeners(handleUserActivity);
    
    return () => {
      if (activityCleanupRef.current) {
        activityCleanupRef.current();
        activityCleanupRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformAdmin, isSessionExpired]);

  const value: PlatformAdminAuthContextType = {
    platformAdmin,
    isAuthenticated: !!platformAdmin,
    isLoading,
    isAuthInitialized,
    login,
    logout,
    verifyToken,
    isSessionExpired,
    navigateToLogin,
  };

  return (
    <PlatformAdminAuthContext.Provider value={value}>
      {children}
    </PlatformAdminAuthContext.Provider>
  );
};

export const usePlatformAdminAuth = (): PlatformAdminAuthContextType => {
  const context = useContext(PlatformAdminAuthContext);
  if (context === undefined) {
    throw new Error('usePlatformAdminAuth must be used within a PlatformAdminAuthProvider');
  }
  return context;
};
